import { useNavigate } from 'react-router';
import TableContainer from '../../../components/table-container/table-container';
import { TableHead } from '../../../components/table-head';
import {
  useGetAllFlaggedMatchChatsQuery,
  useGetAllMatchChatsQuery,
} from '../../../slices/inboxApiSlice';
import TableTd from '../../../components/table-td/table-td';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { ChatMessageTypes, DisputeStatus, GameScreenshotType } from '../../../utils/constants';
import { selectMatchChats } from '../../../slices/inboxSlice';
import HowlingWolf from '../../../components/howling-wolf';
import { dateAndTimeFormatter } from '../../../utils/dateFormatter';
import { useSearchParams } from 'react-router-dom';

const OgmaDashboardInboxMatchChat = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const matchChatrooms = useSelector(selectMatchChats);
  const [showFlagged, setShowFlagged] = useState<boolean>(true);
  const [showThisWeek, setShowThisWeek] = useState<boolean>(false);
  const { isLoading: flaggedIsLoading } = useGetAllFlaggedMatchChatsQuery({});
  const { isLoading } = useGetAllMatchChatsQuery({});

  const isDateInCurrentWeek = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay());
    startOfWeek.setHours(0, 0, 0, 0);
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 7);
    return date >= startOfWeek && date < endOfWeek;
  };

  const params = Object.fromEntries(searchParams.entries());
  const [searchProperty, searchValue] = Object.entries(params)[0] || [null, null];
  const filteredMatchChats = (matchChatrooms ?? []).filter((d: any) => {
    const hasUnreviewedDispute = d.disputes?.some(
      (dispute: any) =>
        dispute.status === DisputeStatus.NotChecked &&
        dispute.type === GameScreenshotType.ChatDispute,
    );
    if (showFlagged && !hasUnreviewedDispute) {
      return false;
    }
    const isThisWeek = isDateInCurrentWeek(d.match_date);
    if (showThisWeek && !isThisWeek) {
      return false;
    }
    if (searchProperty && searchValue) {
      const keyword = searchValue.toLowerCase();

      if (searchProperty === 'team') {
        const teamNames = `${d.teamA?.name ?? ''} ${d.teamB?.name ?? ''}`.toLowerCase();
        if (!teamNames.includes(keyword)) return false;
      } else if (searchProperty === 'code') {
        if (!d.code?.toLowerCase().includes(keyword)) return false;
      }
    }

    return true;
  });

  const tableHeaders = [
    { displayName: 'Season', property: 'name' },
    { displayName: 'Week', property: 'Week' },
    { displayName: 'Teams', property: 'from' },
    { displayName: 'Date', property: 'time' },
  ];

  return (
    <div>
      <button
        className={`px-4 py-1 border-1 my-2 text-sm rounded cursor-pointer ${
          showFlagged ? 'border-red-500' : 'border-gray-300'
        }`}
        onClick={() => {
          setShowFlagged(!showFlagged);
        }}
      >
        Show Flagged
      </button>
      <button
        className={`px-4 py-1 ml-2 border-1 my-2 text-sm rounded cursor-pointer ${
          showThisWeek ? 'border-red-500' : 'border-gray-300'
        }`}
        onClick={() => {
          setShowThisWeek(!showThisWeek);
        }}
      >
        Show This Week
      </button>

      {(isLoading && !showFlagged) || (flaggedIsLoading && !showFlagged) ? (
        <div>
          <HowlingWolf text='Loading Match Chats...' />
        </div>
      ) : filteredMatchChats?.length !== 0 ? (
        <TableContainer>
          <TableHead tableList={tableHeaders.map((l) => l.displayName)} bool={false} />
          <tbody className='bg-white divide-y divide-gray-200'>
            {filteredMatchChats
              ?.slice()
              ?.sort((a: any, b: any) => {
                const getLastTime = (item: any) => {
                  if (item.ChatMessages?.length) {
                    return new Date(item.ChatMessages[item.ChatMessages.length - 1].time).getTime();
                  }
                  return new Date(item.createdAt).getTime();
                };

                return getLastTime(b) - getLastTime(a);
              })
              .map((d: any, i: number) => {
                return (
                  <tr
                    key={i}
                    onClick={() =>
                      d?.SeasonWeek?.Season.SeasonType?.leaderboard
                        ? navigate(
                            `/seasons/view/${d.SeasonWeek?.Season?.id}/chat/${d.id}?week=${d.SeasonWeek?.id}`,
                          )
                        : navigate(`/match/${d.id}`)
                    }
                  >
                    {tableHeaders.map((o, j) => {
                      if (o.property === 'name') {
                        return (
                          <TableTd key={j}>
                            <div className='flex justify-center items-center text-gray-500 text-sm font-semibold'>
                              <div>
                                <div>{d.SeasonWeek?.Season?.name}</div>
                                <div className='flex justify-start text-xs text-gray-400'>
                                  {d.messageType === ChatMessageTypes.MatchChat
                                    ? 'Forfeit Notice'
                                    : ''}
                                </div>
                              </div>
                              &nbsp;
                              {d.unreadMatchChats > 0 && (
                                <span className='text-fen-red'>({d.unreadMatchChats})</span>
                              )}
                              {(() => {
                                const chatroomUser = d.ChatroomUsers?.find(
                                  (cu: any) => cu.userId === d.id,
                                );
                                const lastRead = chatroomUser?.lastReadDate;

                                const unreadCount = d?.messages?.filter((m: any) => {
                                  if (!lastRead) return true;
                                  return new Date(m.time) > new Date(lastRead);
                                }).length;
                                return unreadCount && unreadCount > 0 ? (
                                  <span className='text-fen-red'>({unreadCount})</span>
                                ) : null;
                              })()}
                            </div>
                          </TableTd>
                        );
                      }
                      if (o.displayName === 'Week') {
                        return (
                          <TableTd key={j}>
                            <div className='text-gray-500 text-sm whitespace-normal break-words'>
                              {d.SeasonWeek?.name || 'N/A'}
                            </div>
                          </TableTd>
                        );
                      }
                      if (o.displayName === 'Teams') {
                        return (
                          <TableTd key={j}>
                            <div className='text-gray-500 text-sm whitespace-normal break-words'>
                              {`${d.home_team?.[0]?.teamName ? d.home_team?.[0]?.teamName : ''} ${
                                d.away_team?.[0]?.teamName ? `vs. ${d.away_team?.[0].teamName}` : ''
                              }`}
                            </div>
                          </TableTd>
                        );
                      }
                      if (o.property === 'time') {
                        return (
                          <TableTd key={j}>
                            <div className='text-gray-500 text-sm whitespace-normal break-words'>
                              {dateAndTimeFormatter(
                                d.ChatMessages[d.ChatMessages.length - 1].time,
                              ).split(';')[0] + ''}
                            </div>
                          </TableTd>
                        );
                      }
                      return (
                        <TableTd key={j}>
                          <div className='text-gray-500 test-sm'>{d[o.property]}</div>
                        </TableTd>
                      );
                    })}
                  </tr>
                );
              })}
          </tbody>
        </TableContainer>
      ) : (
        <div className='my-4'>
          <HowlingWolf text='No Match Chats.' />
        </div>
      )}
    </div>
  );
};

export default OgmaDashboardInboxMatchChat;
